import type { JSONContent } from '@tiptap/core';
import { v4 as uuid } from 'uuid';
import { ContentBlockType, ToggleBlock } from '../../model';
import { BlockToTiptapConverter, convertBlocksToTiptapNodes, ConverterContext } from '../blocks-to-tiptap.converter';
import { convertTiptapNodesToBlocks, TiptapToBlockConverter } from '../tiptap-to-blocks.converter';
import { getMarkdownManager } from './utils/markdown-manager.util';

/**
 * Converts a ToggleBlock to a Tiptap details node.
 * Recursively converts nested content blocks.
 *
 * @param block - The ToggleBlock to convert
 * @param context - Optional context to pass through (e.g., 'popover' to skip popover processing)
 * @returns A Tiptap JSON node representing the details/toggle
 */
export const convertToggleBlockToTiptap = ((block, context?: ConverterContext) => {
  return {
    type: 'details',
    attrs: {
      ...(block.custom_css && { custom_css: block.custom_css }),
      ...(block.custom_style && { custom_style: block.custom_style }),
    },
    content: [
      {
        type: 'detailsSummary',
        content: parseMarkdownToInlineContent(block.title),
      },
      {
        type: 'detailsContent',
        content: convertBlocksToTiptapNodes(block.content, context),
      },
    ],
  };
}) satisfies BlockToTiptapConverter<ToggleBlock>;

/**
 * Converts a Tiptap details node back to a ToggleBlock.
 * Recursively converts nested content.
 *
 * @param json - The Tiptap JSON node to convert
 * @param context - Optional converter context to thread through recursive calls
 * @returns A ToggleBlock
 */
export const convertTiptapToToggleBlock = ((json, context?) => {
  const attrs = json.attrs ?? {};
  const summary = json.content?.find((n) => n.type === 'detailsSummary');
  const detailsContent = json.content?.find((n) => n.type === 'detailsContent');

  const title = summary?.content ? serializeInlineContentToMarkdown(summary.content) : '';

  return {
    id: uuid(),
    type: ContentBlockType.Toggle,
    title,
    ...(attrs['custom_css'] && { custom_css: attrs['custom_css'] as string }),
    ...(attrs['custom_style'] && { custom_style: attrs['custom_style'] as Record<string, string> }),
    content: detailsContent?.content?.length ? convertTiptapNodesToBlocks(detailsContent.content, context) : [],
  };
}) satisfies TiptapToBlockConverter;

/**
 * Converts Tiptap inline content nodes (with marks) to a markdown string.
 * Wraps content in a paragraph and doc structure for MarkdownManager serialization.
 */
function serializeInlineContentToMarkdown(nodes: JSONContent[]): string {
  if (!nodes.length) {
    return '';
  }

  const manager = getMarkdownManager();
  const doc: JSONContent = {
    type: 'doc',
    content: [{ type: 'paragraph', content: nodes }],
  };

  // Serialize and trim the result (removes trailing newlines from paragraph)
  return manager.serialize(doc).trim();
}

/**
 * Parses a markdown string into Tiptap inline content nodes.
 * Extracts the content from the first paragraph node.
 */
function parseMarkdownToInlineContent(markdown: string): JSONContent[] {
  if (!markdown) {
    return [{ type: 'text', text: '' }];
  }

  const manager = getMarkdownManager();
  const doc = manager.parse(markdown);

  const inlineContent = doc.content?.flatMap((node) => node.content ?? []);
  return inlineContent?.length ? inlineContent : [{ type: 'text', text: markdown }];
}
